import { useEffect, useState, useRef } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Send, X, Edit3, Trash2, ChefHat, CalendarDays, Users, Sparkles,
  CheckCircle2, MessageSquare, Mail, Loader2, AlertTriangle, Zap,
} from "lucide-react";

export default function AdminSpecials() {
  const [specials, setSpecials] = useState([]);
  const [menu, setMenu] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [s, m] = await Promise.all([api.get("/specials"), api.get("/menu")]);
      setSpecials(s.data || []);
      setMenu(m.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const newOne = () => setEditing({
    title: "", description: "", image_url: "",
    lunch_date: new Date().toISOString().slice(0,10),
    items: [], price: 0, max_orders: null,
  });

  const save = async () => {
    if (!editing.title?.trim()) return toast.error("Title required");
    if (!editing.lunch_date) return toast.error("Lunch date required");
    if (!editing.items?.length) return toast.error("Add at least one menu item");
    setBusy(true);
    try {
      const payload = {
        title: editing.title.trim(),
        description: editing.description || "",
        image_url: editing.image_url || "",
        lunch_date: editing.lunch_date,
        items: (editing.items || []).filter(i => i.name?.trim()).map(i => ({ name: i.name.trim(), qty: i.qty || 1 })),
        price: Number(editing.price) || 0,
        max_orders: editing.max_orders ? Number(editing.max_orders) : null,
        display_until: editing.display_until || null,
      };
      if (editing.id) await api.put(`/specials/${editing.id}`, payload);
      else await api.post("/specials", payload);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const publish = async (sp) => {
    if (!window.confirm(`Broadcast "${sp.title}" via email + SMS to all parents in this school?`)) return;
    try {
      const { data } = await api.post(`/specials/${sp.id}/publish`);
      toast.success(`Sent to ${data.parent_count} parents · ${data.email.sent} emails · ${data.sms.sent} SMS`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const close = async (sp) => {
    if (!window.confirm(`Close "${sp.title}"? Parents will no longer be able to order it.`)) return;
    try { await api.post(`/specials/${sp.id}/close`); toast.success("Closed"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (sp) => {
    if (!window.confirm(`Permanently delete "${sp.title}"?`)) return;
    try { await api.delete(`/specials/${sp.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <AppShell variant="admin">
      <div className="space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand">Special lunches</div>
            <h1 className="font-heading text-4xl font-extrabold text-ink mt-2">Publish a curated meal</h1>
            <p className="text-sm text-ink-500 mt-1">Festive thalis, themed lunches, fundraiser meals — push to every parent's inbox & SMS in one click.</p>
          </div>
          <button data-testid="new-special-btn" onClick={newOne}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand text-white font-semibold hover:bg-brand-700 shadow-soft">
            <Plus size={16} /> New special
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {specials.map(sp => (
            <SpecialCard key={sp.id} sp={sp}
              onEdit={() => setEditing({ ...sp, max_orders: sp.max_orders ?? "", items: (sp.items || []).map(i => ({ menu_item_id: i.menu_item_id, name: i.name, qty: i.qty || 1, price: i.price })) })}
              onPublish={() => publish(sp)} onClose={() => close(sp)} onDelete={() => remove(sp)} />
          ))}
          {specials.length === 0 && (
            <div className="col-span-full bg-white rounded-3xl border border-border p-10 text-center text-ink-500">
              No specials yet — create one to get started.
            </div>
          )}
        </div>
      </div>

      {editing && <SpecialEditor data={editing} setData={setEditing} menu={menu} onClose={() => setEditing(null)} onSave={save} busy={busy} />}
    </AppShell>
  );
}

// --------------------------------------------------
function SpecialCard({ sp, onEdit, onPublish, onClose, onDelete }) {
  const status = sp.status || "draft";
  const tone = status === "published" ? "bg-emerald-100 text-emerald-900" : status === "closed" ? "bg-ink-100 text-ink-700" : "bg-amber-100 text-amber-900";
  const lastBroadcast = (sp.broadcast_log || []).slice(-1)[0];
  return (
    <div className="bg-white rounded-3xl border border-border overflow-hidden flex flex-col" data-testid={`special-card-${sp.id}`}>
      {sp.image_url ? (
        <img src={sp.image_url} alt="" className="w-full h-32 object-cover" />
      ) : (
        <div className="w-full h-32 bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-amber-700"><Sparkles size={28} /></div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="font-heading text-xl font-bold text-ink min-w-0 truncate">{sp.title}</div>
          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tone}`}>{status}</span>
        </div>
        <div className="text-xs text-ink-500 mt-1 inline-flex items-center gap-1"><CalendarDays size={11}/> {sp.lunch_date}</div>
        {sp.description && <p className="text-xs text-ink-600 mt-2 line-clamp-2">{sp.description}</p>}
        <div className="mt-2 text-xs">
          <ul className="space-y-0.5 text-ink-700">
            {(sp.items || []).slice(0, 3).map((i, idx) => <li key={idx}>· {i.qty || 1}× {i.name}</li>)}
            {(sp.items || []).length > 3 && <li className="text-ink-500">…+{sp.items.length - 3} more</li>}
          </ul>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="font-heading text-2xl font-extrabold text-ink">{inr(sp.price)}</div>
          <div className="text-[11px] text-ink-500">
            <span className="font-bold text-ink">{sp.orders_count || 0}</span>/{sp.max_orders ?? "∞"} orders
          </div>
        </div>
        {lastBroadcast && (
          <div className="mt-2 text-[10px] text-ink-500 inline-flex items-center gap-1">
            <CheckCircle2 size={10} className="text-emerald-600"/> Last broadcast: {lastBroadcast.email?.sent || 0} emails · {lastBroadcast.sms?.sent || 0} SMS
            {lastBroadcast.email?.mock && <span className="px-1 py-0.5 rounded bg-amber-100 text-amber-900 ml-1">MOCK</span>}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {status !== "closed" && (
            <button data-testid={`publish-${sp.id}`} onClick={onPublish}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700">
              <Send size={11}/> {status === "published" ? "Re-broadcast" : "Publish"}
            </button>
          )}
          <button data-testid={`edit-${sp.id}`} onClick={onEdit} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cream-200 text-ink-800 text-xs font-bold hover:bg-cream-300"><Edit3 size={11}/> Edit</button>
          {status === "published" && (
            <button data-testid={`close-${sp.id}`} onClick={onClose} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-bold hover:opacity-90">Close</button>
          )}
          <button data-testid={`delete-${sp.id}`} onClick={onDelete} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-50 text-red-900 text-xs font-bold hover:bg-red-100"><Trash2 size={11}/></button>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------
function SpecialEditor({ data, setData, menu, onClose, onSave, busy }) {
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const fileRef = useRef(null);
  const addItem = () => {
    set("items", [...(data.items || []), { name: "", qty: 1 }]);
  };
  const removeItem = (idx) => set("items", data.items.filter((_, i) => i !== idx));
  const setItemField = (idx, field, val) => set("items", data.items.map((i, j) => j === idx ? { ...i, [field]: field === "qty" ? Math.max(1, Number(val) || 1) : val } : i));

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set("image_url", reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 overflow-y-auto py-6" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-3xl p-7 w-full max-w-2xl my-auto" data-testid="special-editor">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand">{data.id ? "Edit special" : "New special"}</div>
            <div className="font-heading text-2xl font-bold text-ink mt-1">Curate the lunch</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-cream-200 inline-flex items-center justify-center"><X size={16}/></button>
        </div>

        <div className="space-y-3">
          <Field label="Title *">
            <input data-testid="se-title" autoFocus value={data.title} onChange={e => set("title", e.target.value)} placeholder="Diwali Festive Thali" className="input" />
          </Field>
          <Field label="Description">
            <textarea data-testid="se-desc" value={data.description} onChange={e => set("description", e.target.value)} placeholder="A celebratory thali with sweets, savoury, and a dessert…" rows={2} className="input" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lunch date *"><input data-testid="se-date" type="date" value={data.lunch_date} onChange={e => set("lunch_date", e.target.value)} className="input" /></Field>
            <Field label="Image">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-2 rounded-xl bg-cream-200 text-ink-700 text-sm font-semibold hover:bg-cream-300">
                  Upload image
                </button>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {data.image_url && <span className="text-xs text-emerald-700">Image set</span>}
              </div>
            </Field>
          </div>

          <Field label={`Items (${data.items?.length || 0})`}>
            <div className="space-y-2">
              {(data.items || []).map((it, idx) => (
                <div key={idx} className="bg-cream-100 rounded-xl px-3 py-2 flex items-center gap-3 text-sm" data-testid={`se-item-${idx}`}>
                  <ChefHat size={14} className="text-brand"/>
                  <input type="text" placeholder="Item name (e.g. Paneer Tikka)" value={it.name || ""} onChange={e => setItemField(idx, "name", e.target.value)}
                    className="flex-1 bg-white rounded-lg border border-border px-2 py-1.5 text-sm focus:border-brand focus:outline-none" />
                  <input type="number" min={1} placeholder="Qty" value={it.qty} onChange={e => setItemField(idx, "qty", e.target.value)} className="w-16 px-2 py-1.5 rounded-lg border border-border text-sm" />
                  <button onClick={() => removeItem(idx)} className="text-ink-400 hover:text-red-500"><X size={14}/></button>
                </div>
              ))}
              <button type="button" onClick={addItem} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:text-brand-700">
                <Plus size={12} /> Add item
              </button>
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Final price (₹) *" hint="What parents pay per cover">
              <input data-testid="se-price" type="number" value={data.price} onChange={e => set("price", e.target.value)} className="input" />
            </Field>
            <Field label="Max covers (optional)" hint="Leave empty for unlimited">
              <input data-testid="se-max" type="number" value={data.max_orders ?? ""} onChange={e => set("max_orders", e.target.value)} className="input" />
            </Field>
          </div>
          <Field label="Display until (order cutoff date)" hint="After this date, parents cannot order this special">
            <input data-testid="se-display-until" type="date" value={data.display_until || ""} onChange={e => set("display_until", e.target.value)} className="input" />
          </Field>

          {data.id && data.status === "published" && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-700 flex-shrink-0 mt-0.5"/>
              <span>This special is already <b>published</b>. Saving changes here updates the record but does not re-broadcast — use the Publish button on the card to re-send.</span>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
          <button data-testid="se-save" disabled={busy} onClick={onSave}
            className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Save
          </button>
        </div>
        <style>{`.input{width:100%;border-radius:12px;border:1px solid var(--border);padding:.5rem .75rem;background:#fff}.input:focus{outline:none;border-color:var(--brand);box-shadow:0 0 0 3px rgba(209,45,45,.15)}`}</style>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wider text-ink-500">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-ink-500 mt-0.5">{hint}</div>}
    </div>
  );
}

function NutritionAIBtn() { return null; }
